import { Controller, type Path, useFormContext } from "react-hook-form";
import BaseFormField from "../../vendor/FormField.jsx";

export type FormFieldProps = {
  /**
   * The type of input field to render.
   *
   * @default text
   */
  type?: "text" | "textarea" | "number" | undefined;

  /**
   * An error message that overrides errors produced by built-in validation.
   */
  error?: { message: string } | undefined;

  accessibleCopyName?: string | undefined;
  ariaLabelText?: string | undefined;
  className?: string | undefined;
  disabled?: boolean | undefined;
  helpText?: React.ReactNode | undefined;
  label?: string | undefined;
  labelPosition?: "top" | "left" | undefined;
  name: string;
  placeholder?: string | number | undefined;
  readonly?: boolean | undefined;
  required?: boolean | undefined;
};

/**
 * <!-- TODO(ndhoule): Document me -->
 *
 * @internal
 */
export const FormField = <
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-parameters
  FieldValues extends Record<string, unknown>,
>({
  labelPosition = "top",
  type = "text",
  name,
  error,
  ...otherProps
}: FormFieldProps) => {
  const { control } = useFormContext<FieldValues>();

  return (
    <Controller<FieldValues>
      name={name as unknown as Path<FieldValues>}
      control={control}
      render={({ fieldState, field: { value, onChange, ...fieldProps } }) => {
        let transformedValue: unknown;
        if (type === "number") {
          if (value == null) {
            transformedValue = value;
          } else if (Number.isNaN(value) || value === 0) {
            transformedValue = "";
          } else {
            transformedValue = value.toString();
          }
        } else {
          transformedValue = value;
        }

        return (
          <BaseFormField
            {...otherProps}
            controlled
            error={error?.message ?? fieldState.error?.message}
            leftLabel={labelPosition === "left"}
            type={type}
            {...fieldProps}
            onChange={(e) => {
              if (type === "number") {
                const output = Number.parseInt(e.target.value, 10);
                onChange(Number.isNaN(output) ? 0 : output);
              } else {
                onChange(e.target.value);
              }
            }}
            value={transformedValue}
          />
        );
      }}
    />
  );
};
